export const PAGE_VISIT_THROTTLE_MS = 30_000;

export type WebPageVisitResolution = {
  pageKey: string;
  surface: 'trainer_dashboard' | 'public_web';
  pathParams?: Record<string, string>;
};

/** @deprecated Use WebPageVisitResolution */
export type TrainerPageVisitResolution = WebPageVisitResolution;

/**
 * Map web pathnames to stable page_key + surface.
 * More-specific patterns first.
 */
export function resolveWebPageKey(
  pathname: string
): WebPageVisitResolution | null {
  const path = pathname.replace(/\/+$/, '') || '/';

  // --- Public / marketing ---
  if (path === '/') {
    return { pageKey: 'public.home', surface: 'public_web' };
  }
  if (path === '/auth') {
    return { pageKey: 'public.auth', surface: 'public_web' };
  }
  if (path === '/terms') {
    return { pageKey: 'public.terms', surface: 'public_web' };
  }
  if (path === '/privacy') {
    return { pageKey: 'public.privacy', surface: 'public_web' };
  }
  let m = path.match(/^\/invite\/([^/]+)$/);
  if (m) {
    return {
      pageKey: 'public.invite',
      surface: 'public_web',
      pathParams: { inviteCode: m[1] },
    };
  }
  if (path === '/personal-trainers') {
    return { pageKey: 'public.personal_trainers', surface: 'public_web' };
  }
  if (path === '/exercises') {
    return { pageKey: 'public.exercises', surface: 'public_web' };
  }
  if (path === '/workout-plans') {
    return { pageKey: 'public.workout_plans', surface: 'public_web' };
  }
  m = path.match(/^\/exercise\/([^/]+)$/);
  if (m) {
    return {
      pageKey: 'public.exercise_detail',
      surface: 'public_web',
      pathParams: { exerciseId: m[1] },
    };
  }

  // --- Trainer dashboard ---
  if (path === '/trainer-dashboard') {
    return { pageKey: 'trainer.dashboard', surface: 'trainer_dashboard' };
  }

  // Legacy orphan calendar URL redirects to member detail — log as detail.
  m = path.match(/^\/trainer-dashboard\/member\/([^/]+)\/calendar$/);
  if (m) {
    return {
      pageKey: 'trainer.member_detail',
      surface: 'trainer_dashboard',
      pathParams: { memberId: m[1] },
    };
  }

  m = path.match(/^\/trainer-dashboard\/member\/([^/]+)\/workout\/([^/]+)$/);
  if (m) {
    return {
      pageKey: 'trainer.member_workout_detail',
      surface: 'trainer_dashboard',
      pathParams: { memberId: m[1], workoutId: m[2] },
    };
  }

  m = path.match(/^\/trainer-dashboard\/member\/([^/]+)$/);
  if (m) {
    return {
      pageKey: 'trainer.member_detail',
      surface: 'trainer_dashboard',
      pathParams: { memberId: m[1] },
    };
  }

  if (path === '/trainer-dashboard/workout-plan/new') {
    return { pageKey: 'trainer.plan_new', surface: 'trainer_dashboard' };
  }

  m = path.match(
    /^\/trainer-dashboard\/workout-plan\/([^/]+)\/workout\/([^/]+)$/
  );
  if (m) {
    return {
      pageKey: 'trainer.plan_workout_detail',
      surface: 'trainer_dashboard',
      pathParams: { planId: m[1], workoutId: m[2] },
    };
  }

  m = path.match(/^\/trainer-dashboard\/workout-plan\/([^/]+)$/);
  if (m) {
    return {
      pageKey: 'trainer.plan_detail',
      surface: 'trainer_dashboard',
      pathParams: { planId: m[1] },
    };
  }

  if (path === '/trainer-dashboard/workout/new') {
    return { pageKey: 'trainer.workout_new', surface: 'trainer_dashboard' };
  }

  m = path.match(/^\/trainer-dashboard\/workout\/([^/]+)$/);
  if (m) {
    return {
      pageKey: 'trainer.workout_detail',
      surface: 'trainer_dashboard',
      pathParams: { workoutId: m[1] },
    };
  }

  if (path === '/chat') {
    return { pageKey: 'trainer.chat', surface: 'trainer_dashboard' };
  }

  return null;
}

/** @deprecated Prefer resolveWebPageKey */
export function resolveTrainerPageKey(
  pathname: string
): WebPageVisitResolution | null {
  const resolved = resolveWebPageKey(pathname);
  if (!resolved || resolved.surface !== 'trainer_dashboard') {
    return null;
  }
  return resolved;
}

type ThrottleEntry = { at: number };

const throttleMap = new Map<string, ThrottleEntry>();

export function resetPageVisitThrottleForTests() {
  throttleMap.clear();
}

export function shouldThrottlePageVisit(
  userId: string,
  pageKey: string,
  path: string,
  nowMs: number = Date.now(),
  windowMs: number = PAGE_VISIT_THROTTLE_MS
): boolean {
  const key = `${userId}|${pageKey}|${path}`;
  const prev = throttleMap.get(key);
  if (prev && nowMs - prev.at < windowMs) {
    return true;
  }
  throttleMap.set(key, { at: nowMs });
  return false;
}
